import React, { useState } from 'react';
import { 
  Shield, Eye, CheckCircle, XCircle, Clock, AlertTriangle,
  Star, TrendingUp, Flame, Award, ExternalLink, Calendar,
  Users, FileText, Globe, Microscope, Search, Filter,
  ChevronDown, ChevronUp, Download, BookOpen, Tag
} from 'lucide-react';

import { useTheme } from '../arrangement/ThemeContext';
const ArticleDetailModal = ({ article, onClose, onApprove, onReject, actionLoading }) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const { colors } = useTheme(); 
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: colors.bgTertiary,
        borderRadius: '20px',
        border: '1px solid #333',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 24px 0 24px',
          borderBottom: '1px solid #333'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <h2 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: '700',
              color: '#e5e5e5',
              lineHeight: '1.3',
              maxWidth: '80%'
            }}>
              {article.title}
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                padding: '8px',
                fontSize: '24px'
              }}
            >
              ✕
            </button>
          </div>

          {article.subtitle && (
            <p style={{
              margin: '0 0 16px 0',
              fontSize: '16px',
              color: '#10b981',
              fontWeight: '500'
            }}>
              {article.subtitle}
            </p>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            padding: '20px 0'
          }}>
            <div>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>Author</div>
              <div style={{ fontSize: '14px', color: '#e5e5e5', fontWeight: '500' }}>{article.author}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>Institution</div>
              <div style={{ fontSize: '14px', color: '#e5e5e5', fontWeight: '500' }}>{article.institution}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>Category</div>
              <div style={{ fontSize: '14px', color: '#e5e5e5', fontWeight: '500' }}>{article.category}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>Published</div>
              <div style={{ fontSize: '14px', color: '#e5e5e5', fontWeight: '500' }}>
                {article.published_date ? new Date(article.published_date).toLocaleDateString() : 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#e5e5e5' }}>
              Description
            </h3>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#bbb',
              lineHeight: '1.6'
            }}>
              {article.description}
            </p>
          </div>

          {/* Article URL */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#e5e5e5' }}>
              Source URL
            </h3>
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                background: colors.bgCard,
                border: '1px solid #333',
                borderRadius: '12px',
                color: '#00d4aa',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#00d4aa';
                e.currentTarget.style.background = '#00d4aa10';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#333';
                e.currentTarget.style.background = '#2a2a2a';
              }}
            >
              <ExternalLink size={14} />
              View Original Article
            </a>
          </div>

          {/* Additional Details */}
          {(article.methodology || article.sample_size || article.sources) && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#e5e5e5' }}>
                Research Details
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '12px'
              }}>
                {article.methodology && (
                  <div style={{
                    padding: '12px',
                    background: colors.bgCard,
                    borderRadius: '8px',
                    border: '1px solid #333'
                  }}>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>Methodology</div>
                    <div style={{ fontSize: '14px', color: '#e5e5e5' }}>{article.methodology}</div>
                  </div>
                )}
                {article.sample_size && (
                  <div style={{
                    padding: '12px',
                    background: colors.bgCard,
                    borderRadius: '8px',
                    border: '1px solid #333'
                  }}>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>Sample Size</div>
                    <div style={{ fontSize: '14px', color: '#e5e5e5' }}>{article.sample_size}</div>
                  </div>
                )}
                {article.sources && (
                  <div style={{
                    padding: '12px',
                    background: colors.bgCard,
                    borderRadius: '8px',
                    border: '1px solid #333'
                  }}>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>Sources</div>
                    <div style={{ fontSize: '14px', color: '#e5e5e5' }}>{article.sources}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#e5e5e5' }}>
                Tags
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {article.tags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '6px 12px',
                      background: '#333',
                      color: '#888',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{
          padding: '24px',
          borderTop: '1px solid #333',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          {!showRejectionForm ? (
            <>
              <button
                onClick={() => setShowRejectionForm(true)}
                disabled={actionLoading}
                style={{
                  padding: '12px 24px',
                  background: 'transparent',
                  color: '#ef4444',
                  border: '2px solid #ef4444',
                  borderRadius: '12px',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <XCircle size={14} />
                Reject Article
              </button>
              
              <button
                onClick={() => onApprove(article.id, {})}
                disabled={actionLoading}
                style={{
                  padding: '12px 24px',
                  background: actionLoading ? '#555' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <CheckCircle size={14} />
                {actionLoading ? 'Processing...' : 'Approve Article'}
              </button>
            </>
          ) : (
            <div style={{ width: '100%' }}>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection (optional)..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '12px',
                  background: colors.bgCard,
                  border: '2px solid #333',
                  borderRadius: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                  marginBottom: '12px'
                }}
              />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowRejectionForm(false);
                    setRejectionReason('');
                  }}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    color: '#888',
                    border: '1px solid #555',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onReject(article.id, rejectionReason);
                    setShowRejectionForm(false);
                    setRejectionReason('');
                  }}
                  disabled={actionLoading}
                  style={{
                    padding: '10px 20px',
                    background: actionLoading ? '#555' : '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export { ArticleDetailModal };